/**
 * Tenant utility functions for managing tenant information
 */

import { API_PATH } from '../config/api.config';
import { apiService } from '../services/api.service';

// Constants
const STORAGE_KEYS = {
  TENANT_ALIAS: 'tenant_alias',
  TENANT_EXPIRY: 'tenant_alias_expiry'
};

// Default expiration time (in milliseconds)
const DEFAULT_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Get the current tenant alias, respecting expiration
 * @param {boolean} ignoreExpiry - Whether to ignore expiration check
 * @returns {Promise<string|null>} The tenant alias or null if not found/expired
 */
export const getTenantAlias = async (ignoreExpiry = false) => {
  // Check for refresh parameter in URL
  const urlParams = new URLSearchParams(window.location.search);
  const forceRefresh = urlParams.get('refresh') !== null;
  
  if (forceRefresh) {
    console.log('Force refresh requested via URL parameter');
    clearTenantData();
    // Continue to domain resolution
    const domainTenant = await getTenantFromDomain();
    if (domainTenant) {
      setTenantAlias(domainTenant);
      return domainTenant;
    }
    return null;
  }
  
  const storedTenantAlias = localStorage.getItem(STORAGE_KEYS.TENANT_ALIAS);
  
  if (!storedTenantAlias) {
    // If no stored tenant, try domain resolution
    const domainTenant = await getTenantFromDomain();
    if (domainTenant) {
      setTenantAlias(domainTenant);
      return domainTenant;
    }
    return null;
  }
  
  // Check expiration if not ignoring
  if (!ignoreExpiry) {
    const expiryTimestamp = localStorage.getItem(STORAGE_KEYS.TENANT_EXPIRY);
    
    if (expiryTimestamp) {
      const now = new Date().getTime();
      
      if (now > parseInt(expiryTimestamp, 10)) {
        console.log('Tenant alias expired, clearing and resolving again');
        clearTenantData();
        
        // Try to resolve from domain
        const domainTenant = await getTenantFromDomain();
        if (domainTenant) {
          setTenantAlias(domainTenant);
          return domainTenant;
        }
        return null;
      }
    }
  }
  
  console.log(`Using stored tenant alias: ${storedTenantAlias}`);
  return storedTenantAlias;
};

/**
 * Set the tenant alias with an expiration time
 * @param {string} alias - The tenant alias to store
 * @param {number} expiryMs - Expiration time in milliseconds
 */
export const setTenantAlias = (alias, expiryMs = DEFAULT_EXPIRY) => {
  if (!alias) return;
  
  localStorage.setItem(STORAGE_KEYS.TENANT_ALIAS, alias);
  
  const expiryTimestamp = new Date().getTime() + expiryMs;
  localStorage.setItem(STORAGE_KEYS.TENANT_EXPIRY, expiryTimestamp.toString());
  
  console.log(`Tenant alias set to ${alias} with expiry in ${expiryMs / (60 * 60 * 1000)} hours`);
};

/**
 * Clear all tenant data from storage
 */
export const clearTenantData = () => {
  localStorage.removeItem(STORAGE_KEYS.TENANT_ALIAS);
  localStorage.removeItem(STORAGE_KEYS.TENANT_EXPIRY);
  console.log('Tenant data cleared from storage');
};

/**
 * Refresh the expiry time for the current tenant alias
 * @param {number} expiryMs - New expiration time in milliseconds
 */
export const refreshTenantExpiry = (expiryMs = DEFAULT_EXPIRY) => {
  const currentAlias = localStorage.getItem(STORAGE_KEYS.TENANT_ALIAS);
  if (currentAlias) {
    setTenantAlias(currentAlias, expiryMs);
    console.log(`Tenant alias expiry refreshed for ${currentAlias}`);
  }
};

/**
 * Get tenant alias from domain name using API
 * @param {number} retryCount - Number of retries attempted
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} retryDelay - Delay between retries in ms
 * @returns {Promise<string|null>} The tenant alias or null if not found
 */
export const getTenantFromDomain = async (retryCount = 0, maxRetries = 3, retryDelay = 1000) => {
  const hostname = window.location.hostname;
  console.log(`Resolving tenant from hostname: ${hostname} (attempt ${retryCount + 1}/${maxRetries + 1})`);
  
  // Default tenant for localhost or 127.0.0.1
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Check for tenant_alias in query string
    const urlParams = new URLSearchParams(window.location.search);
    const tenantAlias = urlParams.get('tenant_alias');
    
    if (tenantAlias) {
      console.log(`Using tenant alias from query string: ${tenantAlias}`);
      return tenantAlias;
    }
    
    console.log('Using default tenant alias for localhost/127.0.0.1: nilecom_363537');
    return 'nilecom_363537';
  }
  
  try {
    // Call the domain resolution API endpoint using the public method
    const response = await apiService.getPublic(`/admin/domains/resolve-public/${hostname}`);
    
    if (response && response.tenant_alias) {
      console.log(`API resolved tenant alias: ${response.tenant_alias} for domain: ${hostname}`);
      return response.tenant_alias;
    } else {
      console.error(`API returned invalid response for domain: ${hostname}`, response);
      
      // Fall back to local resolution for development environments with subdomains
      if (hostname.endsWith('.localhost')) {
        return handleLocalDevelopment(hostname);
      }
      
      return null;
    }
  } catch (error) {
    console.error(`Failed to resolve domain via API: ${hostname}`, error);
    
    // Check if we should retry
    if (retryCount < maxRetries) {
      console.log(`Retrying domain resolution in ${retryDelay}ms...`);
      
      // Wait for the specified delay
      await new Promise(resolve => setTimeout(resolve, retryDelay));
      
      // Exponential backoff for retry delay
      const nextRetryDelay = retryDelay * 2;
      
      // Retry with incremented count
      return getTenantFromDomain(retryCount + 1, maxRetries, nextRetryDelay);
    }
    
    // Fall back to local resolution for development environments with subdomains
    if (hostname.endsWith('.localhost')) {
      return handleLocalDevelopment(hostname);
    }
    
    // All retries failed
    console.error(`Domain resolution failed after ${maxRetries + 1} attempts get default tenant`);
    //TODO: Remove this once we have a proper tenant for the API
    return 'nilecom_363537';
  }
};

/**
 * Handle local development domain resolution
 * @param {string} hostname - The hostname to resolve
 * @returns {string|null} The tenant alias or null
 */
function handleLocalDevelopment(hostname) {
  // Handle localhost with subdomain (e.g., tenant.localhost)
  if (hostname.endsWith('.localhost')) {
    const subdomain = hostname.split('.')[0];
    console.log(`Fallback: Extracted tenant from localhost subdomain: ${subdomain}`);
    return `${subdomain}_dev`;  // Add _dev suffix to indicate development environment
  }
  
  return null;
}

/**
 * Initialize tenant alias from various sources
 * @param {number} expiryMs - Expiration time in milliseconds
 * @returns {Promise<string|null>} The resolved tenant alias or null if not found
 */
export const initializeTenant = async (expiryMs = DEFAULT_EXPIRY) => {
  console.log('Initializing tenant...');
  
  // Check URL parameters first
  const urlParams = new URLSearchParams(window.location.search);
  const tenantParam = urlParams.get('tenant');
  
  if (tenantParam) {
    console.log(`Using tenant from URL parameter: ${tenantParam}`);
    setTenantAlias(tenantParam, expiryMs);
    return tenantParam;
  }
  
  // Check for refresh parameter
  const forceRefresh = urlParams.get('refresh') !== null;
  if (forceRefresh) {
    console.log('Force refresh requested, clearing stored tenant data');
    clearTenantData();
    // Continue to resolve from domain
  } else {
    // If no refresh parameter, check stored value first
    const storedTenant = await getTenantAlias(false);
    if (storedTenant) {
      return storedTenant;
    }
  }
  
  // Resolve from domain
  const domainTenant = await getTenantFromDomain();
  if (domainTenant) {
    console.log(`Using tenant from domain: ${domainTenant}`);
    setTenantAlias(domainTenant, expiryMs);
    return domainTenant;
  }
  
  // If we get here, we couldn't resolve a tenant
  console.error('Could not resolve tenant from any source');
  return null;
};

/**
 * Force refresh the tenant resolution
 * @returns {Promise<string|null>} The newly resolved tenant alias
 */
export const forceTenantRefresh = async () => {
  console.log('Forcing tenant refresh');
  clearTenantData();
  
  // Try to resolve from domain
  const domainTenant = await getTenantFromDomain();
  if (domainTenant) {
    console.log(`Resolved tenant from domain after force refresh: ${domainTenant}`);
    setTenantAlias(domainTenant);
    return domainTenant;
  }
  
  console.error('Failed to resolve tenant after force refresh');
  return null;
};

/**
 * Get full tenant information from the domain resolver API
 * @param {string} hostname - The hostname to resolve
 * @returns {Promise<Object|null>} The tenant information or null if not found
 */
export const getTenantInfo = async (hostname) => {
  try {
    console.log(`Fetching tenant information for hostname: ${hostname}`);
    const response = await apiService.getPublic(`/admin/domains/resolve-public/${hostname}`);
    
    if (response && response.tenant_alias) {
      console.log(`API returned tenant information for domain: ${hostname}`, response);
      return response;
    } else {
      console.error(`API returned invalid response for domain: ${hostname}`, response);
      return null;
    }
  } catch (error) {
    console.error(`Failed to fetch tenant information for domain: ${hostname}`, error);
    return null;
  }
}; 